import React, { useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

interface ConfirmSignupFormProps {
  email: string;
  onConfirmed: (outcome: 'SIGNED_IN' | 'CONFIRMED') => void | Promise<void>;
  onCancel: () => void;
}

/**
 * Email-code entry for a new account, shared by Signup and Login's unconfirmed
 * handoff. Submit and Resend are disabled while a request is in flight: Amplify
 * debounces the auto sign-in call by 300 ms, and a second autoSignIn() inside
 * that window returns a promise that never settles.
 */
const ConfirmSignupForm: React.FC<ConfirmSignupFormProps> = ({ email, onConfirmed, onCancel }) => {
  const { confirmRegistration, resendRegistrationCode } = useAuth();
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  // A ref as well as state: two clicks in the same tick both see isLoading false.
  const inFlight = useRef(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inFlight.current) return;
    inFlight.current = true;
    setIsLoading(true);
    setError('');
    setNotice('');

    try {
      const outcome = await confirmRegistration(email, code.trim());
      await onConfirmed(outcome);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to verify your email. Please try again.');
    } finally {
      inFlight.current = false;
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setIsLoading(true);
    setError('');
    setNotice('');

    try {
      await resendRegistrationCode(email);
      setNotice('A new verification code has been sent to your email.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to send a new code. Please try again.');
    } finally {
      inFlight.current = false;
      setIsLoading(false);
    }
  };

  return (
    <>
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {notice && (
        <div className="alert alert-info" role="status">
          {notice}
        </div>
      )}

      <form onSubmit={handleSubmit} className="login-form">
        <div className="form-group">
          <div className="form-floating">
            <input
              type="text"
              className="form-control"
              id="signupCode"
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              autoComplete="one-time-code"
            />
            <label htmlFor="signupCode">Verification Code</label>
          </div>
        </div>

        <button
          type="submit"
          className="btn btn-primary btn-login"
          disabled={isLoading || !code.trim()}
        >
          {isLoading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Verifying...
            </>
          ) : (
            'Verify Email'
          )}
        </button>

        <div className="reset-actions">
          <button
            type="button"
            className="btn btn-link"
            onClick={handleResend}
            disabled={isLoading}
          >
            Resend Code
          </button>
          <button
            type="button"
            className="btn btn-link btn-cancel"
            onClick={onCancel}
          >
            Back
          </button>
        </div>
      </form>
    </>
  );
};

export default ConfirmSignupForm;
